import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import siteConfig from "../config/siteConfig";
import "../css/about.scss";
import "../css/contactus.scss";

const initialFormData = {
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  subject: "",
  message: "",
};

function Contact() {
  const [formData, setFormData] = useState(initialFormData);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [errors, setErrors] = useState({});
  const submitTimerRef = useRef(null);
  const successTimerRef = useRef(null);

  useEffect(
    () => () => {
      window.clearTimeout(submitTimerRef.current);
      window.clearTimeout(successTimerRef.current);
    },
    []
  );

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData((previous) => ({ ...previous, [name]: value }));
    setShowSuccess(false);

    if (errors[name]) {
      setErrors((previous) => ({ ...previous, [name]: "" }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.firstName.trim())
      newErrors.firstName = "Please enter your first name.";
    if (!formData.lastName.trim())
      newErrors.lastName = "Please enter your last name.";

    if (!formData.email.trim()) {
      newErrors.email = "Please enter your email address.";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Please enter a valid email address.";
    }

    if (!formData.subject) newErrors.subject = "Please choose a subject.";

    if (!formData.message.trim()) {
      newErrors.message = "Please tell us how we can help.";
    } else if (formData.message.trim().length < 10) {
      newErrors.message =
        "Please add a little more detail (at least 10 characters).";
    }

    return newErrors;
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const formErrors = validateForm();

    if (Object.keys(formErrors).length) {
      setErrors(formErrors);
      const firstInvalidField = document.getElementById(
        Object.keys(formErrors)[0]
      );
      firstInvalidField?.scrollIntoView?.({
        behavior: "smooth",
        block: "center",
      });
      firstInvalidField?.focus();
      return;
    }

    setErrors({});
    setShowSuccess(false);
    setIsSubmitting(true);

    submitTimerRef.current = window.setTimeout(() => {
      setFormData(initialFormData);
      setIsSubmitting(false);
      setShowSuccess(true);

      window.requestAnimationFrame(() => {
        document.getElementById("contact-success")?.focus();
      });

      successTimerRef.current = window.setTimeout(
        () => setShowSuccess(false),
        6000
      );
    }, 900);
  };

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

  return (
    <main className="contact info-page" id="main-content">
      <div className="info-shell info-shell--narrow">
        <nav className="info-breadcrumb" aria-label="Breadcrumb">
          <Link to="/" onClick={scrollToTop}>
            Home
          </Link>
          <span aria-hidden="true">/</span>
          <span aria-current="page">Contact</span>
        </nav>

        <header className="info-page__header info-page__hero">
          <p className="info-page__eyebrow">Customer care</p>
          <h1>How can we help?</h1>
          <p className="info-page__lead">
            Questions about a piece, an order or its journey to you? Send us a
            note and our team will respond with thoughtful, practical help.
          </p>
        </header>

        {showSuccess && (
          <div
            className="contact__success"
            id="contact-success"
            role="status"
            tabIndex="-1"
          >
            <span aria-hidden="true">✓</span>
            <div>
              <strong>Your message is on its way.</strong>
              <p>
                Thank you for getting in touch. We will reply during our service
                hours.
              </p>
            </div>
          </div>
        )}

        <div className="contact__layout">
          <aside className="contact__details" aria-label="Contact details">
            <p className="info-page__eyebrow">Direct contact</p>
            <h2>We are here for the details.</h2>
            <div className="contact__channel">
              <span>Email</span>
              <a href={`mailto:${siteConfig.contact.email}`}>
                {siteConfig.contact.email}
              </a>
            </div>

            <div className="contact__channel">
              <span>Business details</span>
              <p>
                <strong>{siteConfig.legalName}</strong>
                <br />
                {siteConfig.contact.addressLines.map((line) => (
                  <React.Fragment key={line}>
                    {line}<br />
                  </React.Fragment>
                ))}
              </p>
            </div>
            <div className="contact__channel">
              <span>Service hours</span>
              <p>{siteConfig.contact.hours}</p>
            </div>
            <p className="contact__note">
              For order questions, include your order number so we can help more
              quickly.
            </p>
          </aside>

          <form className="contact__form" onSubmit={handleSubmit} noValidate>
            <div className="contact__form-intro">
              <p className="info-page__eyebrow">Send a message</p>
              <p>Required fields are marked with an asterisk.</p>
            </div>

            <div className="contact__form-row">
              <div className="contact__field">
                <label htmlFor="firstName">First name *</label>
                <input
                  id="firstName"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  autoComplete="given-name"
                  aria-invalid={Boolean(errors.firstName)}
                  aria-describedby={
                    errors.firstName ? "firstName-error" : undefined
                  }
                />
                {errors.firstName && (
                  <p id="firstName-error" className="contact__error">
                    {errors.firstName}
                  </p>
                )}
              </div>
              <div className="contact__field">
                <label htmlFor="lastName">Last name *</label>
                <input
                  id="lastName"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  autoComplete="family-name"
                  aria-invalid={Boolean(errors.lastName)}
                  aria-describedby={
                    errors.lastName ? "lastName-error" : undefined
                  }
                />
                {errors.lastName && (
                  <p id="lastName-error" className="contact__error">
                    {errors.lastName}
                  </p>
                )}
              </div>
            </div>

            <div className="contact__form-row">
              <div className="contact__field">
                <label htmlFor="email">Email address *</label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  autoComplete="email"
                  aria-invalid={Boolean(errors.email)}
                  aria-describedby={errors.email ? "email-error" : undefined}
                />
                {errors.email && (
                  <p id="email-error" className="contact__error">
                    {errors.email}
                  </p>
                )}
              </div>
              <div className="contact__field">
                <label htmlFor="phone">Phone number</label>
                <input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleInputChange}
                  autoComplete="tel"
                />
              </div>
            </div>

            <div className="contact__field">
              <label htmlFor="subject">Subject *</label>
              <select
                id="subject"
                name="subject"
                value={formData.subject}
                onChange={handleInputChange}
                aria-invalid={Boolean(errors.subject)}
                aria-describedby={errors.subject ? "subject-error" : undefined}
              >
                <option value="">Choose a subject</option>
                <option value="order">My order</option>
                <option value="product">Product and sizing</option>
                <option value="delivery">Delivery or return</option>
                <option value="care">Product care</option>
                <option value="other">Something else</option>
              </select>
              {errors.subject && (
                <p id="subject-error" className="contact__error">
                  {errors.subject}
                </p>
              )}
            </div>

            <div className="contact__field">
              <label htmlFor="message">Message *</label>
              <textarea
                id="message"
                name="message"
                rows="7"
                value={formData.message}
                onChange={handleInputChange}
                placeholder="Order number, item name and anything else that will help us understand your question."
                aria-invalid={Boolean(errors.message)}
                aria-describedby={
                  errors.message ? "message-error" : "message-hint"
                }
              />
              <div className="contact__field-meta">
                {errors.message ? (
                  <p id="message-error" className="contact__error">
                    {errors.message}
                  </p>
                ) : (
                  <p id="message-hint">
                    Please do not include payment card details.
                  </p>
                )}
                <span>{formData.message.length}</span>
              </div>
            </div>

            <button
              className="info-button contact__submit"
              type="submit"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <span className="contact__spinner" aria-hidden="true" />
                  Sending message
                </>
              ) : (
                "Send message"
              )}
            </button>
            <p className="contact__privacy-note">
              By sending this form, you agree that we may use your details to
              answer your request. Read our{" "}
              <Link to="/privacy" onClick={scrollToTop}>
                privacy policy
              </Link>
              .
            </p>
          </form>
        </div>

        <section
          className="info-faq contact__quick-links"
          aria-labelledby="contact-quick-title"
        >
          <div>
            <p className="info-page__eyebrow">Useful now</p>
            <h2 id="contact-quick-title">You may find the answer here.</h2>
          </div>
          <div className="info-faq__list">
            <details>
              <summary>Where is my order?</summary>
              <p>
                Once your order leaves our studio, your dispatch email will
                include the available tracking details.
              </p>
            </details>
            <details>
              <summary>How do I make a return?</summary>
              <p>
                Visit our returns page for the eligibility window, packing
                guidance and refund timing.
              </p>
              <Link
                className="info-text-link"
                to="/returns"
                onClick={scrollToTop}
              >
                View returns
              </Link>
            </details>
          </div>
        </section>
      </div>
    </main>
  );
}

export default Contact;
